import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';

interface SellerCard {
  handle: string;
  name: string;
  avatarUrl?: string;
  rating: number;
  ratingCount: number;
  tier: 'GOLD' | 'TRUSTED' | 'STANDARD' | 'WATCH';
  activeListings: number;
  soldCount: number;
  joinedYear: number;
  previewImages: string[];
}

const tierLabel: Record<SellerCard['tier'], string> = {
  GOLD: 'Gold Seller', TRUSTED: 'Trusted Seller', STANDARD: 'Seller', WATCH: 'New Seller',
};
const tierPill: Record<SellerCard['tier'], string> = {
  GOLD: 'bg-gold text-ink', TRUSTED: 'bg-sage text-cream', STANDARD: 'bg-clay/80 text-cream', WATCH: 'bg-warmline text-mute',
};

/** Directory of real sellers who currently have live listings. */
export default function Sellers() {
  const { data: sellers = [], isLoading } = useQuery<SellerCard[]>({
    queryKey: ['sellers'],
    queryFn: async () => (await api.get('/sellers')).data,
  });

  return (
    <div className="bg-cream min-h-[70vh]">
      <section className="section-dark relative bg-ink text-cream overflow-hidden">
        <div className="absolute inset-0 mashrabiya mashrabiya-soft pointer-events-none" />
        <div className="relative max-w-[1800px] mx-auto px-6 md:px-10 py-14 text-center">
          <div className="stamp text-gold text-[9px]">SELLERS</div>
          <h1 className="display text-4xl md:text-5xl mt-2">Wardrobes worth following.</h1>
          <p className="text-cream/65 text-sm md:text-base mt-3 max-w-[52ch] mx-auto">
            Real Souqli sellers with live listings - every one ID-checked and payment-protected.
          </p>
        </div>
      </section>

      <section className="max-w-[1800px] mx-auto px-6 md:px-10 py-12">
        {isLoading && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {Array.from({ length: 6 }).map((_, i) => <div key={i} className="skeleton h-64 rounded-luxe" />)}
          </div>
        )}

        {!isLoading && !sellers.length && (
          <div className="text-center py-16">
            <div className="display text-2xl text-mute">No sellers yet</div>
            <p className="text-sm text-mute mt-2">Once sellers publish listings, they'll show up here.</p>
          </div>
        )}

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {sellers.map((s) => (
            <div key={s.handle} className="bg-white border border-warmline rounded-luxe overflow-hidden hover:shadow-frame transition-all duration-300 group">
              {!!s.previewImages.length && (
                <div className="grid grid-cols-3 h-28 bg-cream-deep">
                  {s.previewImages.map((src, i) => (
                    <div key={i} className="relative overflow-hidden">
                      <img src={src} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.06] transition-transform duration-700 ease-souq" />
                    </div>
                  ))}
                </div>
              )}
              <div className="p-4">
                <div className="flex items-center gap-3 mb-3">
                  {s.avatarUrl ? (
                    <img src={s.avatarUrl} alt={s.name} className="w-10 h-10 rounded-full object-cover border-2 border-gold/30" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-gold/15 border-2 border-gold/30 grid place-items-center text-sm font-medium text-gold-dark">
                      {s.name.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div className="min-w-0">
                    <div className="font-semibold text-sm text-ink truncate">{s.name}</div>
                    <div className="text-[11px] text-mute truncate">@{s.handle}</div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1.5 mb-3">
                  <span className={`pill text-[9px] ${tierPill[s.tier]}`}>{tierLabel[s.tier]}</span>
                  <span className="pill bg-warmline text-mute text-[9px]">Since {s.joinedYear}</span>
                </div>
                <div className="flex justify-between text-[11px] text-mute mb-4">
                  <span><span className="text-ink font-medium">{s.activeListings}</span> live</span>
                  <span><span className="text-ink font-medium">{s.soldCount}</span> sold</span>
                  <span><span className="text-ink font-medium">★ {s.rating ? s.rating.toFixed(1) : '-'}</span></span>
                </div>
                <Link to={`/seller/${s.handle}`} className="btn btn-outline w-full text-center text-xs py-2">
                  View wardrobe
                </Link>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
